/**
 * ShopPanel — buy consumables (potions/foods + revive kits) with in-game gold.
 *
 * Moved out of the Cube: it's now opened from the "+" button next to the revive
 * kit on the AÇÕES consumables bar (bus event `ui:openShop`). Gold only — never
 * real money.
 *
 * @module ui/panels/ShopPanel
 */

import { BasePanel } from './BasePanel.js';
import { buyFood, buyReviveKit, buyProtectionAmulet } from '../../core/inventory.js';
import { confirmModal } from '../helpers/confirmModal.js';

export class ShopPanel extends BasePanel {
  constructor(opts) {
    super({ name: 'shop', title: 'LOJA', state: opts.state, data: opts.data, hooks: opts.hooks });
    // Chosen quantities survive re-renders (the panel repaints every tick —
    // without this the field snapped back to 1 while the player was typing).
    this._qty = {};
  }

  render() {
    // Don't stomp the qty field mid-interaction: skip tick repaints while the
    // player is focused on an input (gold label refreshes on the next tick).
    const focused = this.body.querySelector?.('.pd-qty-input:focus');
    if (focused) return;

    const gold = this.state.player.gold;

    const foods = Object.entries(this.data.loot.foods ?? {})
      .filter(([, f]) => (f.price ?? 0) > 0);
    const kit = this.data.loot.reviveKit ?? {};
    const amulet = this.data.loot.protectionAmulet ?? {};

    const row = (id, label, sub, price, kind) => {
      const afford = gold >= price;
      const maxQty = Math.max(1, Math.min(999, Math.floor(gold / Math.max(1, price))));
      const qty = Math.max(1, Math.min(maxQty, this._qty[`${kind}:${id}`] ?? 1));
      return `
        <div class="pd-shop-row" style="display:flex;align-items:center;gap:6px;padding:8px;border:1px solid var(--pd-border);border-radius:8px;margin-bottom:6px;background:var(--pd-surface)">
          <div style="flex:1;min-width:0">
            <div style="font-size:12px;font-weight:700;color:var(--pd-text)">${label}</div>
            <div style="font-size:10px;color:var(--pd-muted)">${sub}</div>
          </div>
          <div class="pd-shop-qty" style="display:flex;align-items:center;gap:2px">
            <button class="pd-qty-step" data-step="-1" style="width:20px;height:24px;border:1px solid var(--pd-border);border-radius:6px 0 0 6px;background:rgba(0,0,0,0.3);color:var(--pd-muted);cursor:pointer;font-weight:700">−</button>
            <input class="pd-qty-input" type="number" min="1" max="${maxQty}" value="${qty}" inputmode="numeric"
                   style="width:38px;height:24px;text-align:center;font-size:11px;font-weight:700;border:1px solid var(--pd-border);background:rgba(0,0,0,0.35);color:var(--pd-text);border-radius:0;-moz-appearance:textfield" />
            <button class="pd-qty-step" data-step="1" style="width:20px;height:24px;border:1px solid var(--pd-border);border-radius:0 6px 6px 0;background:rgba(0,0,0,0.3);color:var(--pd-muted);cursor:pointer;font-weight:700">+</button>
          </div>
          <button class="pd-btn pd-btn-${afford ? 'primary' : 'secondary'} pd-shop-buy"
                  data-kind="${kind}" data-id="${id}" data-price="${price}" ${afford ? '' : 'disabled'}
                  style="font-size:11px;padding:6px 10px;white-space:nowrap;min-width:74px">⛁ ${price * qty}</button>
        </div>`;
    };

    this.body.innerHTML = `
      <div style="text-align:center;margin-bottom:10px;font-size:12px">
        Gaste seu <strong style="color:var(--pd-gold)">ouro</strong> em consumíveis.
        <span style="display:block;font-size:12px;margin-top:4px">Ouro atual: <strong style="color:var(--pd-gold)">⛁ ${gold}</strong></span>
      </div>
      <div class="pd-section-title">Poções &amp; Comidas</div>
      ${foods.map(([id, f]) => row(id, f.label ?? id, `Cura ${Math.round((f.healPct ?? 0) * 100)}% da vida`, f.price, 'food')).join('')}
      <div class="pd-section-title" style="margin-top:10px">Reviver</div>
      ${row('reviveKit', kit.label ?? 'Kit de Reviver', 'Revive um herói caído na hora', kit.price ?? 0, 'kit')}
      <div class="pd-section-title" style="margin-top:10px">Masmorra</div>
      ${row('protectionAmulet', amulet.label ?? 'Amuleto de Proteção', `-${Math.round((amulet.reduction ?? 0) * 100)}% dano recebido em uma masmorra`, amulet.price ?? 0, 'amulet')}
    `;

    // Quantity steppers: −/+ adjust the row's input; the buy button live-shows
    // the total price for the chosen amount.
    this.body.querySelectorAll('.pd-shop-row').forEach((rowEl) => {
      const input = rowEl.querySelector('.pd-qty-input');
      const buyBtn = rowEl.querySelector('.pd-shop-buy');
      const unit = Number(buyBtn.dataset.price) || 0;
      const clampQty = () => {
        const max = Number(input.max) || 1;
        input.value = Math.max(1, Math.min(max, Math.floor(Number(input.value) || 1)));
        buyBtn.textContent = `⛁ ${unit * Number(input.value)}`;
        this._qty[`${buyBtn.dataset.kind}:${buyBtn.dataset.id}`] = Number(input.value);
      };
      rowEl.querySelectorAll('.pd-qty-step').forEach((step) => {
        step.addEventListener('click', () => {
          input.value = Number(input.value || 1) + Number(step.dataset.step);
          clampQty();
        });
      });
      input.addEventListener('change', clampQty);
      input.addEventListener('input', clampQty);
      buyBtn.addEventListener('click', () => this._buy(buyBtn.dataset.kind, buyBtn.dataset.id, Number(input.value) || 1));
    });
  }

  /** Confirm in-game, then buy `qty` at once. Never uses native confirm(). */
  async _buy(kind, id, qty = 1) {
    const label = kind === 'kit'
      ? (this.data.loot.reviveKit?.label ?? 'Kit')
      : kind === 'amulet'
      ? (this.data.loot.protectionAmulet?.label ?? 'Amuleto')
      : (this.data.loot.foods[id]?.label ?? 'Item');
    const price = kind === 'kit'
      ? (this.data.loot.reviveKit?.price ?? 0)
      : kind === 'amulet'
      ? (this.data.loot.protectionAmulet?.price ?? 0)
      : (this.data.loot.foods[id]?.price ?? 0);

    const total = price * qty;
    const doc = this.root.ownerDocument;
    const ok = await confirmModal(doc, {
      icon: '🛒',
      title: `Comprar ${qty > 1 ? `${qty}× ` : ''}${label}?`,
      message: `Custa <strong style="color:var(--pd-gold)">⛁ ${total}</strong> de ouro${qty > 1 ? ` (⛁ ${price} cada)` : ''}.`,
      confirmLabel: 'Comprar',
    });
    if (!ok) return;

    let bought;
    if (kind === 'kit') bought = buyReviveKit(this.state, this.data, qty);
    else if (kind === 'amulet') bought = buyProtectionAmulet(this.state, this.data, qty);
    else bought = buyFood(this.state, this.data, id, qty);
    if (!bought) { this._shopToast('Ouro insuficiente.', false); return; }

    this.hooks.onChange?.();
    this.render();
    this._shopToast(`Comprou ${qty > 1 ? `${qty}× ` : ''}${label}!`, true);
  }

  _shopToast(text, ok) {
    const doc = this.root.ownerDocument;
    const msg = doc.createElement('div');
    msg.style.cssText = `position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:var(--pd-surface);border:2px solid ${ok ? 'var(--pd-gold)' : '#e14b4b'};border-radius:12px;padding:16px 24px;text-align:center;z-index:9999;animation:fadeIn .2s`;
    msg.innerHTML = `<div style="font-size:1.1rem;color:${ok ? 'var(--pd-gold)' : '#e14b4b'}">${text}</div>`;
    doc.body.appendChild(msg);
    setTimeout(() => msg.remove(), 1400);
  }
}
